import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, CheckCircle, Loader2,
  Sparkles, Leaf, Waves, Wind, Wrench, User, Search, ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  cat_diarista: { icon: Sparkles, name: "Diarista", bg: "bg-rose-50", text: "text-rose-600" },
  cat_jardinagem: { icon: Leaf, name: "Jardinagem", bg: "bg-emerald-50", text: "text-emerald-600" },
  cat_piscina: { icon: Waves, name: "Piscina", bg: "bg-blue-50", text: "text-blue-600" },
  cat_arcondicionado: { icon: Wind, name: "Ar-Condicionado", bg: "bg-cyan-50", text: "text-cyan-600" },
  cat_reforma: { icon: Wrench, name: "Reforma", bg: "bg-amber-50", text: "text-amber-600" }
};

const FREQUENCIES = [
  { id: "weekly", label: "Semanal", desc: "Toda semana" },
  { id: "biweekly", label: "Quinzenal", desc: "A cada 2 semanas" },
  { id: "monthly", label: "Mensal", desc: "Uma vez por mês" }
];

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

export default function NewSubscriptionPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  
  const [formData, setFormData] = useState({
    category_id: "",
    provider_id: "",
    frequency: "weekly",
    day_of_week: 1,
    day_of_month: 1,
    preferred_time: "09:00",
    base_price: "",
    address: { city: "", state: "SP", neighborhood: "" },
    notes: ""
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        if (res.ok) setCategories((await res.json()).filter(c => c.supports_recurrence));
      } catch (e) { console.error(e); }
    };
    fetchCategories();
  }, []);

  const searchProviders = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/providers/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ query: searchQuery, search_type: "name" })
      });
      if (res.ok) setProviders(await res.json());
    } catch (e) { toast.error("Erro ao buscar"); }
    finally { setSearching(false); }
  };

  const handleSubmit = async () => {
    if (!formData.category_id || !formData.base_price || !formData.address.city) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...formData,
          day_of_week: formData.frequency !== "monthly" ? formData.day_of_week : null,
          day_of_month: formData.frequency === "monthly" ? formData.day_of_month : null,
          base_price: parseFloat(formData.base_price)
        })
      });
      if (res.ok) {
        toast.success("Assinatura criada!");
        navigate("/subscriptions");
      } else throw new Error((await res.json()).detail);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const selectedCategory = categories.find(c => c.category_id === formData.category_id);
  const config = CATEGORY_CONFIG[formData.category_id];

  return (
    <div className="min-h-screen-mobile bg-white pb-8" data-testid="new-subscription-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate("/subscriptions")}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 touch-target"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-700" />
            </button>
            <span className="font-semibold text-neutral-900">Nova assinatura</span>
          </div>
          <span className="text-sm text-neutral-500">{step}/4</span>
        </div>
      </header>

      {/* Progress */}
      <div className="px-5 py-3">
        <div className="flex gap-1.5">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-neutral-900" : "bg-neutral-200"}`} />
          ))}
        </div>
      </div>

      <main className="px-5 py-6">
        {/* Step 1: Category */}
        {step === 1 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Qual serviço?</h2>
              <p className="text-neutral-500 text-sm mt-1">Selecione o tipo de serviço recorrente</p>
            </div>

            <div className="space-y-3">
              {categories.map(cat => {
                const cfg = CATEGORY_CONFIG[cat.category_id];
                const Icon = cfg?.icon || Sparkles;
                const selected = formData.category_id === cat.category_id;
                return (
                  <button
                    key={cat.category_id}
                    onClick={() => setFormData({ ...formData, category_id: cat.category_id })}
                    className={`list-item w-full ${selected ? "border-neutral-900" : ""}`}
                    data-testid={`category-${cat.category_id}`}
                  >
                    <div className={`icon-box ${cfg?.bg}`}>
                      <Icon className={`w-5 h-5 ${cfg?.text}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-neutral-900">{cat.name}</p>
                      <p className="text-neutral-500 text-sm line-clamp-1">{cat.description}</p>
                    </div>
                    {selected && <CheckCircle className="w-5 h-5 text-neutral-900" />}
                  </button>
                );
              })}
            </div>

            <Button 
              className="w-full h-12 btn-minimal btn-primary rounded-xl"
              onClick={() => setStep(2)}
              disabled={!formData.category_id}
            >
              Continuar
            </Button>
          </div>
        )}

        {/* Step 2: Frequency */}
        {step === 2 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Com que frequência?</h2>
              <p className="text-neutral-500 text-sm mt-1">Escolha a periodicidade</p>
            </div>

            <div className="space-y-3">
              {FREQUENCIES.map(freq => {
                const selected = formData.frequency === freq.id;
                return (
                  <button
                    key={freq.id}
                    onClick={() => setFormData({ ...formData, frequency: freq.id })}
                    className={`list-item w-full ${selected ? "border-neutral-900" : ""}`}
                    data-testid={`frequency-${freq.id}`}
                  >
                    <div className={`icon-box ${selected ? "bg-neutral-900" : "bg-neutral-100"}`}>
                      <RefreshCw className={`w-5 h-5 ${selected ? "text-white" : "text-neutral-600"}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-neutral-900">{freq.label}</p>
                      <p className="text-neutral-500 text-sm">{freq.desc}</p>
                    </div>
                    {selected && <CheckCircle className="w-5 h-5 text-neutral-900" />}
                  </button>
                );
              })}
            </div>

            {formData.frequency !== "monthly" ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">Dia da semana</label>
                <div className="flex gap-2">
                  {WEEKDAYS.map((day, i) => (
                    <button
                      key={i}
                      onClick={() => setFormData({ ...formData, day_of_week: i })}
                      className={`flex-1 h-10 rounded-lg text-sm font-medium transition-colors ${
                        formData.day_of_week === i ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-700"
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">Dia do mês</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={formData.day_of_month}
                  onChange={(e) => setFormData({ ...formData, day_of_month: parseInt(e.target.value) })}
                  className="input-minimal"
                />
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-700">Horário</label>
              <input
                type="time"
                value={formData.preferred_time}
                onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                className="input-minimal"
              />
            </div>

            <Button 
              className="w-full h-12 btn-minimal btn-primary rounded-xl"
              onClick={() => setStep(3)}
            >
              Continuar
            </Button>
          </div>
        )}

        {/* Step 3: Provider & Price */}
        {step === 3 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Prestador e valor</h2>
              <p className="text-neutral-500 text-sm mt-1">Opcional: escolha um prestador específico</p>
            </div>

            <div className="card-subtle p-4 space-y-3">
              <label className="text-sm font-medium text-neutral-700">Buscar prestador</label>
              <div className="flex gap-2">
                <div className="input-with-icon flex-1">
                  <Search className="input-icon w-4 h-4" />
                  <input
                    placeholder="Nome do prestador..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && searchProviders()}
                    className="input-minimal"
                  />
                </div>
                <Button onClick={searchProviders} disabled={searching} className="h-[52px] px-4 btn-minimal btn-secondary">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buscar"}
                </Button>
              </div>

              {providers.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {providers.map(p => (
                    <button
                      key={p.provider_id}
                      onClick={() => setFormData({ ...formData, provider_id: p.provider_id })}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                        formData.provider_id === p.provider_id ? "border-neutral-900 bg-neutral-50" : "border-neutral-200"
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-neutral-500" />
                      </div>
                      <span className="text-sm font-medium text-neutral-900">{p.user?.name || "Prestador"}</span>
                      {formData.provider_id === p.provider_id && <CheckCircle className="w-4 h-4 text-neutral-900 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-700">Valor por serviço (R$) *</label>
              <input
                type="number"
                placeholder="0,00"
                value={formData.base_price}
                onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                className="input-minimal text-xl font-semibold"
                data-testid="price-input"
              />
              {selectedCategory && (
                <p className="text-xs text-neutral-500">Sugerido: R$ {selectedCategory.base_price_min} - R$ {selectedCategory.base_price_max}</p>
              )}
            </div>

            <Button 
              className="w-full h-12 btn-minimal btn-primary rounded-xl"
              onClick={() => setStep(4)}
              disabled={!formData.base_price}
            >
              Continuar
            </Button>
          </div>
        )}

        {/* Step 4: Address & Confirm */}
        {step === 4 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Endereço</h2>
              <p className="text-neutral-500 text-sm mt-1">Onde o serviço será realizado</p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <label className="text-sm font-medium text-neutral-700">Cidade *</label>
                  <input
                    value={formData.address.city}
                    onChange={(e) => setFormData({ ...formData, address: { ...formData.address, city: e.target.value }})}
                    placeholder="São Paulo"
                    className="input-minimal"
                    data-testid="city-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-700">UF</label>
                  <input
                    value={formData.address.state}
                    onChange={(e) => setFormData({ ...formData, address: { ...formData.address, state: e.target.value }})}
                    placeholder="SP"
                    className="input-minimal"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">Bairro</label>
                <input
                  value={formData.address.neighborhood}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, neighborhood: e.target.value }})}
                  placeholder="Centro"
                  className="input-minimal"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700">Observações</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Instruções especiais..."
                  rows={3}
                  className="input-minimal resize-none py-3"
                />
              </div>
            </div>

            {/* Summary */}
            <div className="card-dark p-5 space-y-3">
              <p className="font-medium text-white">Resumo</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-neutral-400">
                  <span>Serviço</span>
                  <span className="text-white">{config?.name}</span>
                </div>
                <div className="flex justify-between text-neutral-400">
                  <span>Frequência</span>
                  <span className="text-white">{FREQUENCIES.find(f => f.id === formData.frequency)?.label}</span>
                </div>
                <div className="flex justify-between text-neutral-400">
                  <span>Valor</span>
                  <span className="text-white font-semibold">R$ {parseFloat(formData.base_price || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <Button 
              className="w-full h-12 btn-minimal btn-primary rounded-xl"
              onClick={handleSubmit}
              disabled={submitting || !formData.address.city}
              data-testid="submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Criar assinatura
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
